import React,{useState,useEffect,useCallback,useRef} from 'react';
import {customerAPI,branchAPI,uploadAPI} from '../utils/api';
import {PageHeader,SearchBar,DataTable,Badge,Modal,FormInput,fmt,fmtDate,FilterButtons} from '../components/common';
import {useLang} from '../i18n';
import {Plus,Edit,Trash2,ToggleLeft,ToggleRight,Ban,MapPin,Camera,
  Upload,ExternalLink,X,RefreshCw,User} from 'lucide-react';
import toast from 'react-hot-toast';
import {useAuth} from '../context/AuthContext';
import {ShieldOff} from 'lucide-react';

const NoPerm=()=>(<div style={{display:'flex',alignItems:'center',gap:8,padding:'10px 14px',background:'var(--dm)',borderRadius:8,fontSize:12,color:'var(--danger)',marginBottom:12}}><ShieldOff size={14}/> Access denied</div>);

// ── Photo Capture ────────────────────────────────────────────────
function PhotoCapture({value,onChange}){
  const[mode,setMode]=useState('idle');
  const[stream,setStream]=useState(null);
  const[uploading,setUploading]=useState(false);
  const videoRef=useRef();
  const canvasRef=useRef();
  const fileRef=useRef();

  const startCamera=async()=>{
    try{
      const s=await navigator.mediaDevices.getUserMedia({video:{facingMode:'user',width:640,height:480}});
      setStream(s);setMode('camera');
      setTimeout(()=>{if(videoRef.current){videoRef.current.srcObject=s;videoRef.current.play();}},100);
    }catch{toast.error('Camera not available — please upload a photo instead.');}
  };

  const stopCamera=()=>{
    if(stream){stream.getTracks().forEach(t=>t.stop());setStream(null);}
    setMode('idle');
  };

  const capture=()=>{
    if(!videoRef.current||!canvasRef.current) return;
    const v=videoRef.current;const c=canvasRef.current;
    c.width=v.videoWidth||640;c.height=v.videoHeight||480;
    c.getContext('2d').drawImage(v,0,0);
    stopCamera();
    c.toBlob(async blob=>{
      if(!blob) return;
      setUploading(true);
      try{
        const file=new File([blob],'customer-photo.jpg',{type:'image/jpeg'});
        const r=await uploadAPI.single(file);
        onChange(r.data.url);toast.success('Photo saved');
      }catch{toast.error('Upload failed');}
      finally{setUploading(false);}
    },'image/jpeg',0.85);
  };

  const handleFile=async e=>{
    const file=e.target.files?.[0];if(!file) return;
    if(file.size>5*1024*1024) return toast.error('Max 5MB');
    setUploading(true);
    try{const r=await uploadAPI.single(file);onChange(r.data.url);toast.success('Photo uploaded');}
    catch{toast.error('Upload failed');}
    finally{setUploading(false);e.target.value='';}
  };

  return(
    <div>
      <label style={{display:'block',fontSize:11,fontWeight:700,color:'var(--td)',textTransform:'uppercase',letterSpacing:.4,marginBottom:8}}>
        Customer Photo
      </label>

      {/* Photo preview */}
      {value&&(
        <div style={{position:'relative',display:'inline-block',marginBottom:10}}>
          <img src={value} alt="Customer" style={{width:110,height:110,borderRadius:12,objectFit:'cover',border:'2px solid var(--primary)',display:'block'}}/>
          <button type="button" onClick={()=>onChange('')}
            style={{position:'absolute',top:-6,right:-6,width:20,height:20,borderRadius:'50%',background:'var(--danger)',border:'none',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',padding:0}}>
            <X size={11}/>
          </button>
        </div>
      )}

      {/* Placeholder when no photo */}
      {!value&&mode==='idle'&&(
        <div style={{width:110,height:110,borderRadius:12,border:'2px dashed var(--border)',background:'var(--s2)',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',marginBottom:10,gap:6}}>
          <User size={28} color="var(--td)"/>
          <span style={{fontSize:9,color:'var(--td)',fontWeight:600}}>NO PHOTO</span>
        </div>
      )}

      {/* Camera viewfinder */}
      {mode==='camera'&&(
        <div style={{marginBottom:10,borderRadius:10,overflow:'hidden',border:'2px solid var(--primary)',position:'relative',display:'inline-block'}}>
          <video ref={videoRef} autoPlay playsInline muted style={{width:260,height:195,display:'block',objectFit:'cover'}}/>
          <div style={{position:'absolute',bottom:8,left:0,right:0,display:'flex',justifyContent:'center',gap:8}}>
            <button type="button" className="btn btn-primary btn-sm" onClick={capture} style={{gap:5,fontSize:11}}>
              <Camera size={12}/> Snap
            </button>
            <button type="button" className="btn btn-ghost btn-sm" onClick={stopCamera} style={{fontSize:11}}>Cancel</button>
          </div>
        </div>
      )}
      <canvas ref={canvasRef} style={{display:'none'}}/>

      {/* Buttons */}
      {mode!=='camera'&&(
        <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
          <button type="button" className="btn btn-ghost btn-sm" onClick={startCamera} style={{gap:4,fontSize:11}}>
            <Camera size={12}/> Camera
          </button>
          <label className="btn btn-ghost btn-sm" style={{cursor:'pointer',gap:4,fontSize:11}}>
            <Upload size={12}/> Upload
            <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} style={{display:'none'}}/>
          </label>
          {uploading&&<span style={{fontSize:11,color:'var(--td)',display:'flex',alignItems:'center',gap:4}}>
            <RefreshCw size={11} style={{animation:'spin 1s linear infinite'}}/> Saving...
          </span>}
        </div>
      )}
    </div>
  );
}

// ── Location Link ────────────────────────────────────────────────
function LocationInput({value,onChange}){
  const isGoogleMap=v=>v&&(
    v.includes('google.com/maps')||v.includes('goo.gl/maps')||
    v.includes('maps.app.goo.gl')||v.includes('maps.google')
  );
  return(
    <div>
      <label style={{display:'block',fontSize:11,fontWeight:700,color:'var(--td)',textTransform:'uppercase',letterSpacing:.4,marginBottom:5}}>
        Location (Google Maps Link)
      </label>
      <div style={{position:'relative'}}>
        <MapPin size={14} color={value?'var(--primary)':'var(--td)'}
          style={{position:'absolute',left:10,top:'50%',transform:'translateY(-50%)',pointerEvents:'none'}}/>
        <input
          type="url" value={value||''} onChange={e=>onChange(e.target.value)}
          placeholder="Paste Google Maps share link..."
          className="form-input"
          style={{paddingLeft:32,paddingRight:value?60:12}}
        />
        {value&&(
          <div style={{position:'absolute',right:8,top:'50%',transform:'translateY(-50%)',display:'flex',gap:6,alignItems:'center'}}>
            <a href={value} target="_blank" rel="noopener noreferrer"
              title="Open in Google Maps"
              style={{color:'var(--primary)',display:'flex',alignItems:'center'}}>
              <ExternalLink size={14}/>
            </a>
            <button type="button" onClick={()=>onChange('')}
              style={{background:'none',border:'none',cursor:'pointer',color:'var(--td)',display:'flex',alignItems:'center',padding:0}}>
              <X size={13}/>
            </button>
          </div>
        )}
      </div>
      {value&&isGoogleMap(value)&&(
        <div style={{marginTop:5,fontSize:10,color:'var(--success)',display:'flex',alignItems:'center',gap:3}}>
          <MapPin size={10}/> Valid Google Maps link ✓
        </div>
      )}
      {value&&!isGoogleMap(value)&&(
        <div style={{marginTop:5,fontSize:10,color:'var(--warning)'}}>
          ⚠ Paste a Google Maps share link (tap Share → Copy link on Google Maps)
        </div>
      )}
    </div>
  );
}

// ── Main Page ────────────────────────────────────────────────────
export default function CustomersPage(){
  const{t}=useLang();
  const{hasPermission,isOwner,myBranchId,myBranch}=useAuth();
  const can={view:hasPermission('customers.view'),create:hasPermission('customers.create'),edit:hasPermission('customers.edit'),delete:hasPermission('customers.delete'),export:hasPermission('customers.export')};
  const[data,setData]=useState([]);const[branches,setBranches]=useState([]);const[groups,setGroups]=useState([]);
  const[ld,setLd]=useState(true);const[search,setSearch]=useState('');const[typeFilter,setTypeFilter]=useState('All');
  const[modal,setModal]=useState(null);const[form,setForm]=useState({});const[delId,setDelId]=useState(null);

  const loadMeta=async()=>{try{const[b,g]=await Promise.all([branchAPI.getAll({limit:100}),customerAPI.getGroups()]);setBranches(b.data.data);setGroups(g.data.data||g.data)}catch{}};
  const load=useCallback(async()=>{try{const p={search,limit:200};if(typeFilter!=='All')p.type=typeFilter;const r=await customerAPI.getAll(p);setData(r.data.data)}catch{toast.error(t('common.failedToLoad'))}finally{setLd(false)}},[search,typeFilter]);
  useEffect(()=>{loadMeta()},[]);
  useEffect(()=>{const tt=setTimeout(load,300);return()=>clearTimeout(tt)},[load]);

  const branchOpts=[{value:'',label:t('common.all')},...branches.map(b=>({value:String(b.id),label:b.name}))];
  const groupOpts=[{value:'',label:t('common.all')},...groups.map(g=>({value:String(g.id),label:g.name}))];
  const blank={name:'',email:'',phone:'',address:'',city:'',type:'Individual',group_id:'',credit_limit:0,discount_percent:0,birthday:'',notes:'',is_active:true,branch_id:'',location_url:'',photo_url:''};
  const F=(k,v)=>setForm(f=>({...f,[k]:v}));

  const save=async()=>{
    if(!form.name)return toast.error(t('customers.nameRequired'));
    try{
      const d={...form,credit_limit:parseFloat(form.credit_limit)||0,discount_percent:parseFloat(form.discount_percent)||0,group_id:parseInt(form.group_id)||null,branch_id:parseInt(form.branch_id)||null};
      if(modal==='new'){await customerAPI.create(d);toast.success(t('customers.created'))}
      else{await customerAPI.update(form.id,d);toast.success(t('customers.updated'))}
      setModal(null);load();
    }catch(e){toast.error(e.response?.data?.error||t('common.failedToSave'));}
  };
  const toggle=async c=>{try{await customerAPI.update(c.id,{is_active:!c.is_active});toast.success(c.is_active?'Deactivated':'Activated');load()}catch{toast.error('Failed')}};
  const toggleBlacklist=async c=>{try{await customerAPI.update(c.id,{is_blacklisted:!c.is_blacklisted});toast.success(c.is_blacklisted?'Removed from blacklist':'Blacklisted');load()}catch{toast.error('Failed')}};
  const del=async()=>{try{await customerAPI.delete(delId);toast.success(t('common.deleted'));setDelId(null);load()}catch(e){toast.error(e.response?.data?.error||'Failed')}};

  if(!can.view)return<NoPerm/>;
  return(<div>
    {!isOwner&&myBranch&&(
      <div style={{display:'flex',alignItems:'center',gap:8,padding:'9px 14px',background:'var(--pm)',borderRadius:10,marginBottom:14,fontSize:12}}>
        <span>🔒</span><span style={{color:'var(--primary)',fontWeight:600}}>{t('branches.viewingBranch',{name:myBranch.name})}</span>
      </div>
    )}
    <PageHeader title={t('customers.title')} subtitle={`${data.length} ${t('customers.title').toLowerCase()}`} action={can.create&&<button className="btn btn-primary" onClick={()=>{setForm(blank);setModal('new')}}><Plus size={16}/> New Customer</button>}/>
    <div className="filter-bar">
      <div className="flex-1" style={{minWidth:200}}><SearchBar value={search} onChange={setSearch} placeholder={t('customers.searchPlaceholder')}/></div>
      <FilterButtons options={[t('common.all'),t('customers.business'),t('customers.individual')]} active={typeFilter} onChange={setTypeFilter}/>
    </div>

    <DataTable loading={ld} columns={[
      {key:'photo',label:'',render:r=>r.photo_url
        ?<img src={r.photo_url} alt="" style={{width:36,height:36,borderRadius:8,objectFit:'cover',border:'1px solid var(--border)'}}/>
        :<div style={{width:36,height:36,borderRadius:8,background:'var(--s2)',display:'flex',alignItems:'center',justifyContent:'center',border:'1px solid var(--border)'}}><User size={15} color="var(--td)"/></div>
      },
      {key:'name',label:t('customers.columns.customer'),render:r=>(
        <div>
          <div className="font-bold">{r.name}{r.is_blacklisted&&<span style={{color:'var(--danger)',marginLeft:6,fontSize:10}}>⛔ BLOCKED</span>}</div>
          <div className="text-dim" style={{fontSize:11}}>{r.email}</div>
          {r.location_url&&(
            <a href={r.location_url} target="_blank" rel="noopener noreferrer"
              style={{display:'inline-flex',alignItems:'center',gap:3,fontSize:10,color:'var(--primary)',marginTop:1,textDecoration:'none'}}>
              <MapPin size={9}/> Map
            </a>
          )}
        </div>
      )},
      {key:'type',label:t('customers.columns.type'),render:r=><Badge variant={r.type==='Business'?'primary':'accent'}>{r.type}</Badge>},
      {key:'phone',label:t('customers.columns.phone')},
      {key:'group',label:t('customers.columns.group'),render:r=>r.group?.name||'—'},
      {key:'branch',label:t('customers.columns.branch'),render:r=>r.branch?.name||'—'},
      {key:'credit_limit',label:t('customers.columns.creditLimit'),render:r=>fmt(r.credit_limit)},
      {key:'current_balance',label:t('customers.columns.balance'),render:r=><span style={{color:parseFloat(r.current_balance)>0?'var(--warning)':'var(--success)',fontWeight:600}}>{fmt(r.current_balance)}</span>},
      {key:'loyalty_points',label:t('customers.columns.points'),render:r=><span className="text-accent font-bold">{r.loyalty_points||0}</span>},
      {key:'created_by',label:'Created By',render:r=>r.creator
        ?<div><div style={{fontSize:12,fontWeight:600}}>{r.creator.first_name} {r.creator.last_name}</div><div style={{fontSize:10,color:'var(--td)'}}>{fmtDate(r.createdAt)}</div></div>
        :<div><div style={{fontSize:11,color:'var(--td)'}}>—</div><div style={{fontSize:10,color:'var(--td)'}}>{fmtDate(r.createdAt)}</div></div>
      },
      {key:'is_active',label:t('customers.columns.status'),render:r=><Badge variant={r.is_active?'success':'danger'}>{r.is_active?t('common.active'):t('common.inactive')}</Badge>},
    ]} data={data} actions={r=>(<div className="flex gap-2">
      {can.edit&&<button className="btn btn-ghost btn-sm" onClick={()=>toggle(r)}>{r.is_active?<ToggleRight size={16} color="var(--success)"/>:<ToggleLeft size={16}/>}</button>}
      {can.edit&&<button className="btn btn-ghost btn-sm" onClick={()=>toggleBlacklist(r)}><Ban size={14} color={r.is_blacklisted?'var(--danger)':undefined}/></button>}
      {can.edit&&<button className="btn btn-ghost btn-sm" onClick={()=>{setForm({...r,group_id:r.group_id||r.group?.id||'',branch_id:r.branch_id||r.branch?.id||'',location_url:r.location_url||'',photo_url:r.photo_url||''});setModal('edit')}}><Edit size={14}/></button>}
      {can.delete&&<button className="btn btn-danger btn-sm" onClick={()=>setDelId(r.id)}><Trash2 size={14}/></button>}
      {!can.edit&&!can.delete&&<span style={{fontSize:11,color:'var(--td)'}}>{t('common.viewOnly')}</span>}
    </div>)}/>

    {/* CREATE / EDIT */}
    <Modal open={modal==='new'||modal==='edit'} onClose={()=>setModal(null)} title={modal==='new'?t('customers.newCustomer'):t('customers.editCustomer')} width={700}>

      {/* Photo + basic info row */}
      <div style={{display:'flex',gap:20,marginBottom:16,alignItems:'flex-start'}}>
        <div style={{flexShrink:0}}>
          <PhotoCapture value={form.photo_url||''} onChange={v=>F('photo_url',v)}/>
        </div>
        <div style={{flex:1}}>
          <div className="form-grid">
            <FormInput label={t('customers.name')} value={form.name||''} onChange={v=>F('name',v)} required/>
            <FormInput label={t('common.phone')} value={form.phone||''} onChange={v=>F('phone',v)}/>
            <FormInput label={t('common.email')} value={form.email||''} onChange={v=>F('email',v)}/>
            <FormInput label={t('customers.type')} value={form.type||'Individual'} onChange={v=>F('type',v)} options={[{value:'Business',label:t('customers.business')},{value:'Individual',label:t('customers.individual')}]}/>
          </div>
        </div>
      </div>

      {/* Location link */}
      <div style={{marginBottom:14}}>
        <LocationInput value={form.location_url||''} onChange={v=>F('location_url',v)}/>
      </div>

      {/* Other fields */}
      <div className="form-grid">
        <FormInput label={t('customers.group')} value={String(form.group_id||'')} onChange={v=>F('group_id',v)} options={groupOpts}/>
        {isOwner?(
          <FormInput label={t('common.branch')} value={String(form.branch_id||'')} onChange={v=>F('branch_id',v)} options={branchOpts}/>
        ):(
          <div>
            <label style={{display:'block',fontSize:11,fontWeight:700,color:'var(--td)',textTransform:'uppercase',letterSpacing:.4,marginBottom:5}}>Branch</label>
            <div style={{display:'flex',alignItems:'center',gap:6,padding:'10px 12px',background:'var(--pm)',border:'1.5px solid var(--primary)',borderRadius:8,fontSize:13,fontWeight:600,color:'var(--primary)'}}>
              {t('common.set')} {myBranch?.name||t('common.yourBranch')}
            </div>
          </div>
        )}
        <FormInput label={t('customers.creditLimit')} value={String(form.credit_limit||0)} onChange={v=>F('credit_limit',v)} type="number"/>
        <FormInput label={t('customers.discountPercent')} value={String(form.discount_percent||0)} onChange={v=>F('discount_percent',v)} type="number"/>
        <FormInput label={t('customers.birthday')} value={form.birthday||''} onChange={v=>F('birthday',v)} type="date"/>
        <FormInput label={t('common.city')} value={form.city||''} onChange={v=>F('city',v)}/>
      </div>
      <FormInput label={t('common.addr')} value={form.address||''} onChange={v=>F('address',v)} type="textarea"/>
      <FormInput label={t('common.notes')} value={form.notes||''} onChange={v=>F('notes',v)} type="textarea"/>

      <div className="modal-footer">
        <button className="btn btn-ghost" onClick={()=>setModal(null)}>Cancel</button>
        <button className="btn btn-primary" onClick={save}>Save</button>
      </div>
    </Modal>

    {/* DELETE */}
    <Modal open={!!delId} onClose={()=>setDelId(null)} title={t('customers.deleteCustomer')} width={400}>
      <p className="text-muted" style={{marginBottom:18}}>{t('common.deleteConfirm')}</p>
      <div className="modal-footer">
        <button className="btn btn-ghost" onClick={()=>setDelId(null)}>Cancel</button>
        <button className="btn btn-danger" onClick={del}>Delete</button>
      </div>
    </Modal>
  </div>);
}
